import { useEffect } from 'react';
import type { ApolloClient } from 'apollo-client';
import { useQuery, type QueryHookOptions } from '@apollo/react-hooks';
import type { Extension, GQLExtension } from '../graphql/types';
import { isGQLGatewayError, isGQLUnderlyingServiceError } from '../graphql/types';
import { useMetricsContext } from '../../context';
import { FetchPreFilteredForgeAppsQuery } from './lib/queries';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
import { createAtlAttributionHeader } from '../../utils';
import type {
	PreFilteredExtensionsQueryHookOptions,
	UsePreFilteredExtensionListHookResult,
} from './useExtensionList';
import { transformPreFilteredExtensions } from './lib/transformForgeExtensions';

export interface PrefilteredExtensionsResult {
	confluence_forgeExtensionsByType?: GQLExtension[];
}

export interface PreFilteredExtensionsVariables {
	cloudId: string;
	context: GQLExtensionRenderingContextInput;
	includeHidden: boolean;
	locale?: string;
	types: string[];
}

export type UsePreFilteredExtensionsQueryOptions = PreFilteredExtensionsVariables & {
	client?: ApolloClient<object>;
	queryOptions?: UsePreFilteredExtensionsQueryOptions;
};

export type PreFilteredExtensionsQueryOptions = QueryHookOptions<
	PrefilteredExtensionsResult,
	PreFilteredExtensionsVariables
>;

export interface PrefilteredExtensionsGQLResult<T> {
	data?: { confluence_forgeExtensionsByType?: T[] };
	loading: boolean;
}

export type GQLExtensionRenderingContextInput = {
	contentId?: string;
	spaceKey?: string;
};

export const createUsePrefilteredExtensionListQueryOptions = <T>(
	cloudId: string,
	context: GQLExtensionRenderingContextInput,
	client: ApolloClient<object> | undefined,
	types: string[],
	locale?: string,
	includeHidden?: boolean,
	queryOptions?: PreFilteredExtensionsQueryHookOptions,
	contextIds: string[] = [cloudId],
): T => {
	const variables = {
		cloudId,
		context,
		includeHidden: includeHidden ?? false,
		types,
		locale,
	};

	return {
		variables,
		...queryOptions,
		client,
		/**
		 * We don't want to set 'ignore' because that discards errors and we need to inspect
		 * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
		 * https://www.apollographql.com/docs/react/data/error-handling/
		 */
		errorPolicy: 'all',
		context: {
			headers: {
				'atl-attribution': createAtlAttributionHeader(contextIds.sort().join()),
			},
			allowOnExternalPage: true,
		},
	} as unknown as T;
};

export const usePrefilteredExtensionList = ({
	cloudId,
	context,
	includeHidden,
	locale,
	types,
	client,
	queryOptions,
}: UsePreFilteredExtensionsQueryOptions): UsePreFilteredExtensionListHookResult => {
	const requestOptions =
		createUsePrefilteredExtensionListQueryOptions<UsePreFilteredExtensionsQueryOptions>(
			cloudId,
			context,
			client,
			types,
			locale,
			includeHidden,
			queryOptions,
		);

	const { error, loading, data } = useQuery<
		PrefilteredExtensionsResult,
		PreFilteredExtensionsQueryOptions
	>(FetchPreFilteredForgeAppsQuery, requestOptions);
	const extensions = getPrefilteredExtensionsGQLResult({ loading, data });

	const { page } = useMetricsContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (extensions) {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'succeeded',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'usePrefilteredExtensionList',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, page, createAnalyticsEvent]);

	useEffect(() => {
		if (
			// if hydration of appOwners (non-critical data) fails but we still have the extensions,
			// we shouldn't count this as a bad event
			!extensions &&
			error &&
			error.graphQLErrors.length > 0
		) {
			const err = error.graphQLErrors.find(
				(err) => isGQLUnderlyingServiceError(err) || isGQLGatewayError(err),
			);

			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.webClient',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'usePrefilteredExtensionList',
						errorName: err
							? isGQLUnderlyingServiceError(err)
								? 'componentApi'
								: 'componentGraphQL'
							: 'uncaught',
					},
				},
			}).fire(FORGE_UI_ANALYTICS_CHANNEL);
		}
	}, [extensions, error, page, createAnalyticsEvent]);

	return {
		error,
		extensions: extensions || [],
		loading,
	};
};

export const getPrefilteredExtensionsGQLResult = <T>({
	loading,
	data,
}: PrefilteredExtensionsGQLResult<T>): Extension[] | null => {
	const extensions = (data?.confluence_forgeExtensionsByType || []) as Extension[];
	if (!loading && extensions.length > 0) {
		return transformPreFilteredExtensions(extensions);
	}
	return null;
};
